import {createEstablishedAhpSessionBootstrap} from '@github-ui/ahp-session-client/bootstrap'
import type {
  EstablishedAhpDirectTurnTelemetry,
  EstablishedAhpSessionBootstrap,
  EstablishedAhpSessionIdentity,
  EstablishedAhpSessionProvisioned,
  EstablishedAhpSessionRelayDiagnostics,
  EstablishRequest,
  ProvisionRequest,
} from '@github-ui/ahp-session-client/contracts'
import type {AcquireRelayLeaseOptions} from '@github-ui/ahp-relay/relay-registry'
import type {ClientTool} from '@github-ui/ahp-relay/relay-connection-runtime'
import {HOST_INDEPENDENT_CLIENT_TOOLS} from '@github-ui/copilot-shared-ui/utils/ahp-client-tools'
import {createActivationTraceCapturingFetcher} from './activation-trace'

export interface MissionControlAhpSessionRef {
  taskId: string
  environmentId: string
  sessionId: string
  channel?: string
}

/**
 * The daemon builds its client tool-handler map once at session creation and
 * cannot accept tools afterwards. `none` never contributes tools, avoiding an
 * active-client claim that could displace another surface merely by mounting.
 * Eligible interactive Chat surfaces claim their tools separately at the input
 * dispatch boundary. `baseline-at-create` contributes the baseline only with
 * `createSession`, while `host-bound` keeps its baseline and host handlers
 * available on every establish mode.
 */
type HostTools = {kind: 'none'} | {kind: 'baseline-at-create'} | {kind: 'host-bound'; tools: readonly ClientTool[]}

type MissionControlRelayOptions = Omit<
  AcquireRelayLeaseOptions,
  'environmentId' | 'sessionId' | 'channel' | 'taskId' | 'clientTools'
> &
  Partial<Pick<AcquireRelayLeaseOptions, 'channel' | 'taskId'>> & {
    clientTools?: never
    hostTools: HostTools
  }
type MissionControlAdapterRequest = Exclude<EstablishRequest, {mode: 'resume-provision'}>

export interface MissionControlEstablishedAhpBootstrapOptions {
  provisionSession(input: ProvisionRequest): Promise<MissionControlAhpSessionRef>
  createRelayOptions(
    identity: EstablishedAhpSessionIdentity,
    request?: MissionControlAdapterRequest,
  ): MissionControlRelayOptions
  attachSession?(identity: EstablishedAhpSessionIdentity): Promise<MissionControlAhpSessionRef>
  wakeSession?(identity: EstablishedAhpSessionIdentity): Promise<MissionControlAhpSessionRef>
  recoverSession?(identity: EstablishedAhpSessionIdentity): Promise<MissionControlAhpSessionRef>
  directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry
  onReset?: () => void
  onDispose?: () => void
}

function identityFor(session: MissionControlAhpSessionRef): EstablishedAhpSessionIdentity {
  return {
    taskId: session.taskId,
    environmentId: session.environmentId,
    sessionId: session.sessionId,
    ...(session.channel ? {channel: session.channel} : {}),
  }
}

function sessionForIdentity(identity: EstablishedAhpSessionIdentity): MissionControlAhpSessionRef {
  return {
    taskId: identity.taskId ?? identity.sessionId,
    environmentId: identity.environmentId,
    sessionId: identity.sessionId,
    ...(identity.channel ? {channel: identity.channel} : {}),
  }
}

function provisioned(
  session: MissionControlAhpSessionRef,
  createRelayOptions: MissionControlEstablishedAhpBootstrapOptions['createRelayOptions'],
  request: MissionControlAdapterRequest,
  relayDiagnostics: EstablishedAhpSessionRelayDiagnostics,
  directTurnTelemetry: EstablishedAhpDirectTurnTelemetry | undefined,
): EstablishedAhpSessionProvisioned {
  const identity = identityFor(session)
  const {hostTools, ...relayOptions} = createRelayOptions(identity, request)
  const contributesBaseline =
    hostTools.kind === 'host-bound' ||
    (hostTools.kind === 'baseline-at-create' && relayOptions.createSession !== undefined)
  const clientTools: ClientTool[] = contributesBaseline ? [...HOST_INDEPENDENT_CLIENT_TOOLS] : []
  const clientToolNames = new Set(clientTools.map(tool => tool.definition.name))
  if (hostTools.kind === 'host-bound') {
    for (const tool of hostTools.tools) {
      if (clientToolNames.has(tool.definition.name)) continue
      clientToolNames.add(tool.definition.name)
      clientTools.push(tool)
    }
  }
  return {
    identity,
    relay: {
      ...relayOptions,
      fetcher: createActivationTraceCapturingFetcher(relayOptions.fetcher, {
        taskId: identity.taskId ?? identity.sessionId,
        sessionId: identity.sessionId,
        environmentId: identity.environmentId,
      }),
      ...relayDiagnostics,
      environmentId: identity.environmentId,
      sessionId: identity.sessionId,
      ...(identity.channel ? {channel: identity.channel} : {}),
      ...(identity.taskId ? {taskId: identity.taskId} : {}),
      clientTools,
    },
    ...(directTurnTelemetry ? {directTurnTelemetry} : {}),
  }
}

/**
 * Mission Control owns task eligibility, history, wakeability, and token
 * wiring. Once it resolves a live AHP identity, this constructor hands it to
 * the same established-session bootstrap used by Quick Chat.
 */
export function createMissionControlEstablishedAhpBootstrap(
  options: MissionControlEstablishedAhpBootstrapOptions,
): EstablishedAhpSessionBootstrap {
  let relayDiagnostics: EstablishedAhpSessionRelayDiagnostics = {}
  let disposed = false
  const sharedBootstrap = createEstablishedAhpSessionBootstrap({
    provision: async input =>
      provisioned(
        await options.provisionSession(input),
        options.createRelayOptions,
        {mode: 'provision', input},
        relayDiagnostics,
        options.directTurnTelemetry,
      ),
    attach: async identity =>
      provisioned(
        await (options.attachSession?.(identity) ?? Promise.resolve(sessionForIdentity(identity))),
        options.createRelayOptions,
        {mode: 'attach', identity},
        relayDiagnostics,
        options.directTurnTelemetry,
      ),
    wake: async identity =>
      provisioned(
        await (options.wakeSession?.(identity) ?? Promise.resolve(sessionForIdentity(identity))),
        options.createRelayOptions,
        {mode: 'wake', identity},
        relayDiagnostics,
        options.directTurnTelemetry,
      ),
    recover: async identity =>
      provisioned(
        await (options.recoverSession?.(identity) ?? Promise.resolve(sessionForIdentity(identity))),
        options.createRelayOptions,
        {mode: 'recover', identity},
        relayDiagnostics,
        options.directTurnTelemetry,
      ),
  })

  return {
    get state() {
      return sharedBootstrap.state
    },
    get session() {
      return sharedBootstrap.session
    },
    ...(options.directTurnTelemetry ? {directTurnTelemetry: options.directTurnTelemetry} : {}),
    get provisionedIdentity() {
      return sharedBootstrap.provisionedIdentity
    },
    configureRelayDiagnostics: diagnostics => {
      relayDiagnostics = diagnostics
    },
    establish: request => sharedBootstrap.establish(request),
    cancelPendingFirstTurn: () => sharedBootstrap.cancelPendingFirstTurn(),
    reset: () => {
      if (disposed) return
      sharedBootstrap.reset()
      options.directTurnTelemetry?.reset('replaced')
      options.onReset?.()
    },
    dispose: () => {
      if (disposed) return
      disposed = true
      sharedBootstrap.dispose()
      options.directTurnTelemetry?.reset('disposed')
      options.onDispose?.()
    },
  }
}
